import { muiTheme } from '@inform-appshell/mui-appshell';
import { createTheme, type Theme } from '@mui/material';

// GB30 names both families bare, so an unresolvable face falls back to the browser serif rather
// than to something of the same character. Duplicated from the webapp: the two packages share no
// source module, and a third workspace package is not worth two constants (see ADR-0023).
const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const MONO_STACK = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** Every already-materialised variant carries its own `fontFamily`, so overriding the top-level key
 *  through `createTheme`'s second argument would leave `body1`, `h1` and friends on bare `Inter`. */
function restackFonts(typography: Theme['typography']): Record<string, unknown> {
  const entries = Object.entries(typography).map(([key, value]) => [
    key,
    value && typeof value === 'object' && 'fontFamily' in value ? { ...value, fontFamily: FONT_STACK } : value,
  ]);
  return { ...Object.fromEntries(entries), fontFamily: FONT_STACK };
}

/** Same chrome surface as the webapp's standalone chrome — see `chromeSurface` there. */
const chromeSurface = (theme: Theme) => ({
  backgroundImage: 'none',
  color: (theme.vars || theme).palette.text.primary,
  backgroundColor: 'var(--surface-ground)',
  ...theme.applyStyles('dark', { backgroundColor: 'var(--surface-sunken)' }),
});

/**
 * The shell's theme: mui-appshell's GB30 theme plus the font fallbacks.
 *
 * Built on the package's `muiTheme` rather than `createMuiTheme` directly, so the shell picks up the
 * dialog padding its own `FeedbackHost` and confirm dialogs expect. Palette, typography and spacing
 * stay GB30's — both apps emit the same unprefixed `:root` block, so a divergence here would restyle
 * whichever app mounted second (ADR-0023).
 */
export const shellTheme: Theme = createTheme(muiTheme, {
  typography: restackFonts(muiTheme.typography),
  components: {
    // GB30 hardcodes the families here too, outside typography.
    MuiChip: { styleOverrides: { root: { fontFamily: FONT_STACK } } },
    MuiTableCell: { styleOverrides: { root: { fontFamily: MONO_STACK } } },
    MuiAppBar: { styleOverrides: { root: ({ theme }: { theme: Theme }) => chromeSurface(theme) } },
    MuiDrawer: { styleOverrides: { paper: ({ theme }: { theme: Theme }) => chromeSurface(theme) } },
    // Same dark-mode overlay fix as the webapp's MuiDialog — see the rationale there.
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }: { theme: Theme }) => ({
          backgroundImage: 'none',
          border: `1px solid ${(theme.vars || theme).palette.divider}`,
        }),
      },
    },
  },
});
